import * as babylon from "@babylonjs/core";

import { isAbortError } from "@/shared/errors";
import { ErrorMonitoringService } from "@/shared/services/ErrorMonitoringService";
import * as schema from "@/shared/state/visualization/schema/v1/three-d";
import { TopicData } from "@/shared/state/visualization/schema/v1/topic-data";

import {
  messagePathKey,
  TopicIdToMessagePaths,
} from "../../../DataServer/messaging";
import * as components from "../../components";
import { markLoadFailed } from "../../components/reloadable";
import { BabylonContext, BabylonState } from "../../state";

import { quaternionFor } from "./quaternionFor";
import { invalidateGlobalPositions } from "./reloadGlobalPositions";
import { extractValues } from "./reloadVectorFields";

/**
 * Checks for stale rotations, and loads their caches if needed.
 *
 * The rotational twin of `reloadStalePositions`, and invalidating the same way:
 * a descendant's absolute pose is composed through this entity's orientation, so
 * changing it invalidates every global below.
 */
export function reloadStaleRotations(state: BabylonState) {
  for (const entity of Object.values(state.entities)) {
    if (!entity.rotation || entity.rotation.cache.load.state !== "stale") {
      continue;
    }
    const load = entity.rotation.cache.load;
    load.abortController.abort();

    load.state = "loading";
    const abortController = new AbortController();
    load.abortController = abortController;
    entity.rotation.cache.allRotations = [];
    invalidateGlobalPositions(state, entity.id);
    reloadCache(entity.rotation, state.context, abortController.signal).catch(
      (e) => {
        if (isAbortError(e) && abortController.signal.aborted) {
          return;
        }
        ErrorMonitoringService.captureError(e);
        markLoadFailed(load, abortController.signal);
      },
    );
  }
}

/**
 * The channels a rotation reads, in the order `quaternionFor` expects them.
 *
 * Each is a constant or a bound topic, with the scale that converts its units —
 * a joint publishing degrees becomes radians. A quaternion's components take no
 * scale, since scaling them is not a rotation.
 */
function channelsOf(
  spec: schema.Rotation,
): { value: TopicData | number; scale: number }[] {
  switch (spec.type) {
    case "axis_angle":
      return [{ value: spec.angle, scale: spec.angleScale }];
    case "euler":
      return [
        { value: spec.roll, scale: spec.rollScale },
        { value: spec.pitch, scale: spec.pitchScale },
        { value: spec.yaw, scale: spec.yawScale },
      ];
    case "quaternion":
      return [
        { value: spec.x, scale: 1 },
        { value: spec.y, scale: 1 },
        { value: spec.z, scale: 1 },
        { value: spec.w, scale: 1 },
      ];
  }
}

/**
 * Loads the given rotation's cache.
 *
 * Takes the component rather than the whole state, like `reloadPositions` does,
 * so a rotation replaced during an async gap cannot have this write land on it.
 */
async function reloadCache(
  rotation: components.Rotation,
  context: BabylonContext,
  signal: AbortSignal,
) {
  const channels = channelsOf(rotation.spec);

  // Nothing bound: one constant orientation, no fetch.
  if (channels.every((channel) => typeof channel.value === "number")) {
    const quaternion = quaternionFor(
      rotation.spec,
      channels.map((channel) => (channel.value as number) * channel.scale),
    );
    rotation.current = { logtime: 0n, quaternion };
    rotation.cache.logtimes = [0n];
    rotation.cache.allRotations = [quaternion];
    rotation.cache.load.state = "loaded";
    return;
  }

  const topicIdToMessagePaths: TopicIdToMessagePaths = {};
  for (const { value } of channels) {
    if (typeof value === "number") {
      continue;
    }
    const mp = {
      id: value.messagePath.id,
      parts: value.messagePath.parts,
    };
    topicIdToMessagePaths[value.topic.id] ??= {};
    topicIdToMessagePaths[value.topic.id][messagePathKey(mp)] = mp;
  }

  const topicIdToMessagePathValues = await context.dataServer.getAllData(
    topicIdToMessagePaths,
    signal,
  );
  signal.throwIfAborted();

  const sampled = channels.map((channel) =>
    extractValues(topicIdToMessagePathValues, channel.value, channel.scale),
  );
  const { logtimes, values } = mergeChannelTimelines(sampled);

  rotation.cache.logtimes = logtimes;
  rotation.cache.allRotations = values.map((sample) =>
    quaternionFor(rotation.spec, sample),
  );
  rotation.current = {
    logtime: logtimes[0],
    quaternion: rotation.cache.allRotations[0] ?? babylon.Quaternion.Identity(),
  };
  rotation.cache.load.state = "loaded";
}

/**
 * Merges any number of independent channel timelines into one.
 *
 * The n-channel form of `mergeComponentTimelines`, which does this for exactly
 * the three of a position. Sample-and-hold: each channel's latest value is held
 * forward until its next sample arrives, so a rotation whose angles come from
 * topics logged at different rates still yields one orientation per instant that
 * any of them changed.
 *
 * Exported for its own test — the index bookkeeping is the part that goes wrong.
 */
export function mergeChannelTimelines(
  channels: { logtimes: bigint[]; values: number[] }[],
): { logtimes: bigint[]; values: number[][] } {
  const logtimes: bigint[] = [];
  const values: number[][] = [];
  if (channels.length === 0) {
    return { logtimes, values };
  }

  const cursors = channels.map(() => 0);
  for (;;) {
    // The earliest time any channel still has a sample for.
    let time: bigint | undefined;
    for (const [index, channel] of channels.entries()) {
      const next = channel.logtimes[cursors[index]];
      if (next !== undefined && (time === undefined || next < time)) {
        time = next;
      }
    }
    if (time === undefined) {
      break;
    }

    // Advance every channel that has a sample at this instant, then read all of
    // them — a channel with nothing new holds the value it last had.
    for (const [index, channel] of channels.entries()) {
      if (channel.logtimes[cursors[index]] === time) {
        cursors[index] += 1;
      }
    }
    logtimes.push(time);
    values.push(
      channels.map((channel, index) => {
        const held = Math.max(0, cursors[index] - 1);
        return channel.values[held] ?? channel.values[0] ?? 0;
      }),
    );
  }

  return { logtimes, values };
}
